import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import { 
  Shield, CheckCircle, Loader2, Menu, X,
  ExternalLink, Copy, Check, ChevronDown, TrendingUp, Wifi, Users, Clock
} from 'lucide-react';
import { 
  RocketIcon, TargetIcon, KeyIcon, DiamondIcon, PhoneIcon, 
  LaptopIcon, MonitorIcon, BulbIcon, LockIcon, GlobeIcon, 
  LightningIcon, FreeIcon, SparkleIcon, PartyIcon, RefreshIcon,
  UsersIcon, DownloadIcon
} from './icons/CustomIcons';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VpnGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState('');
  const [progress, setProgress] = useState(0);
  const [generationTime, setGenerationTime] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // const [timeLeft, setTimeLeft] = useState('');
  const [showHidezenAd, setShowHidezenAd] = useState(false);
  const [hidezenConfig, setHidezenConfig] = useState(null);
  const [userCountry, setUserCountry] = useState(null);
  const [userDevice, setUserDevice] = useState(null);
  const { toast } = useToast();

  // Check geotargeting on component mount
  useEffect(() => {
    checkGeotargeting();
  }, []);

  const checkGeotargeting = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      if (!backendUrl) {
        console.warn('Backend URL not configured - skipping geotargeting');
        return;
      }

      const response = await axios.post(`${backendUrl}/api/geotargeting/check`, {}, {
        timeout: 5000, // 5 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Only show Hidezen if explicitly confirmed by backend
      if (response.data && response.data.show_hidezen === true && response.data.hidezen_url && response.data.hidezen_message) {
        setShowHidezenAd(true);
        setHidezenConfig({
          url: response.data.hidezen_url,
          message: response.data.hidezen_message,
          hidezen_button_text: response.data.hidezen_button_text
        });
      }
      
      // Set country and device info if available (but don't fail if missing)
      if (response.data && response.data.country) {
        setUserCountry(response.data.country);
      }
      if (response.data && response.data.device) {
        setUserDevice(response.data.device);
      }
      
    } catch (error) {
      // Comprehensive error handling - never show errors to user, never show Hidezen on errors
      if (error.code === 'ECONNABORTED') {
        console.warn('Geotargeting API timeout - proceeding with normal VPN generation');
      } else if (error.response) {
        console.warn(`Geotargeting API error (${error.response.status}) - proceeding with normal VPN generation`);
      } else if (error.request) {
        console.warn('Geotargeting API network error - proceeding with normal VPN generation');
      } else {
        console.warn('Geotargeting check error - proceeding with normal VPN generation');
      }
      
      // Ensure Hidezen is not shown on any error
      setShowHidezenAd(false);
      setHidezenConfig(null);
      setUserCountry(null);
      setUserDevice(null);
    }
  };

  useEffect(() => {
    // Safely check geotargeting on component mount
    // If any error occurs, just proceed with normal VPN generation
    const safeCheckGeotargeting = async () => {
      try {
        await checkGeotargeting();
      } catch (error) {
        console.warn('Error during initial geotargeting check - proceeding with normal VPN generation');
        // Ensure clean state on error
        setShowHidezenAd(false);
        setHidezenConfig(null);
      }
    };
    
    safeCheckGeotargeting();
  }, []);

  // Countdown timer removed

  const forceShowHidezen = () => {
    setShowHidezenAd(true);
    setHidezenConfig({
      url: 'https://hidezen.com/ru/',
      message: '🎉 Эксклюзивное предложение! Получите 90 дней бесплатного премиум VPN с максимальной скоростью и безопасностью.',
      buttonText: '🚀 Активировать премиум VPN бесплатно',
      button_text: '🚀 Активировать премиум VPN бесплатно',
      hidezen_button_text: '🚀 Активировать премиум VPN бесплатно'
    });
    setUserCountry({ name: 'Ukraine', code: 'UA' });
    setUserDevice({ os: 'macOS' });
    setGeneratedKey('HIDEZEN_PROMO');
  };

  const generateVpnKey = async () => {
    // Very smooth and slow auto-scroll to generation area after button click
    setTimeout(() => {
      const startPosition = window.pageYOffset;
      const targetPosition = 600;
      const distance = targetPosition - startPosition;
      const duration = 2500; // 2.5 seconds for very slow scroll
      let start = null;

      function smoothScroll(timestamp) {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        const ease = Math.min(progress / duration, 1);
        
        // Easing function for very smooth animation
        const easeInOutCubic = ease < 0.5 
          ? 4 * ease * ease * ease 
          : (ease - 1) * (2 * ease - 2) * (2 * ease - 2) + 1;
        
        window.scrollTo(0, startPosition + (distance * easeInOutCubic));
        
        if (progress < duration) {
          requestAnimationFrame(smoothScroll);
        }
      }
      
      requestAnimationFrame(smoothScroll);
    }, 300);

    // SAFETY: Only show Hidezen if all conditions are safe and valid
    try {
      if (showHidezenAd && hidezenConfig && hidezenConfig.url && hidezenConfig.message) {
        setGeneratedKey('');
        setIsGenerating(true);
        setProgress(0);
        
        // Show progress animation
        const interval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 100) {
              clearInterval(interval);
              setIsGenerating(false);
              
              // Show Hidezen promotion
              setGeneratedKey('HIDEZEN_PROMO');
              return 100;
            }
            return prev + (Math.random() * 15 + 5);
          });
        }, 300);
        
        return;
      }
    } catch (error) {
      console.warn('Error in Hidezen display logic - proceeding with normal VPN generation');
      // Fall through to normal generation
    }

    // Original VPN key generation logic - always works as fallback
    generateNormalVpnKey();
  };

  const generateNormalVpnKey = async () => {
    setIsGenerating(true);
    setProgress(0);
    setGeneratedKey('');

    try {
      // Start progress simulation
      const startTime = Date.now();
      const estimatedTime = Math.floor(Math.random() * 3) + 3; // 3-5 seconds
      setGenerationTime(estimatedTime);

      // Animate progress bar
      const progressInterval = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        const newProgress = Math.min((elapsed / estimatedTime) * 100, 95);
        setProgress(newProgress);
      }, 100);

      // Wait for the estimated time before making API call (3-5 seconds)
      await new Promise(resolve => setTimeout(resolve, estimatedTime * 1000));

      const response = await axios.post(`${API}/generate-vpn-key`);
      const keyData = response.data;

      clearInterval(progressInterval);
      setProgress(100);

      // Show maintenance message instead of key
      if (keyData.success) {
        setGeneratedKey('MAINTENANCE_MESSAGE');
        toast({
          title: "Уведомление",
          description: keyData.message || "Техническое обслуживание",
        });
      } else {
        throw new Error(keyData.error || keyData.message || "Не удалось показать сообщение");
      }
    } catch (error) {
      console.error('Key generation failed:', error);
      toast({
        title: "Ошибка",
        description: error.response?.data?.message || error.message || "Не удалось сгенерировать ключ. Попробуйте еще раз.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: "Скопировано!",
        description: "VPN ключ скопирован в буфер обмена",
      });
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      setCopied(true);
      toast({
        title: "Скопировано!",
        description: "VPN ключ скопирован в буфер обмена",
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Download links for VPN clients
  const downloadLinks = {
    windows: "https://hidezen.com/ru/",
    macos: "https://hidezen.com/ru/", 
    android: "https://hidezen.com/ru/",
    vps: "https://serva.one"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-800/50 backdrop-blur-xl bg-slate-900/20">
        <div className="container mx-auto py-4 px-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity group">
                <div className="relative">
                  <Shield className="h-8 w-8 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
                  <div className="absolute -inset-1 bg-cyan-400/20 rounded-full blur-sm group-hover:blur-md transition-all"></div>
                </div>
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  VPNKey.me
                </h1>
              </a>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6 text-sm">
              <a href="https://geomyip.com" 
                 target="_blank"
                 rel="noopener noreferrer"
                 className="flex items-center space-x-2 text-green-400 hover:text-green-300 transition-colors bg-slate-800/50 px-4 py-2 rounded-lg hover:bg-slate-700/50 backdrop-blur-sm group font-semibold">
                <GlobeIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span>Мой IP</span>
              </a>
              <span className="font-medium text-slate-300">Скачать VPN клиент:</span>
              <a href={downloadLinks.windows} 
                 className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors bg-slate-800/50 px-3 py-2 rounded-lg hover:bg-slate-700/50 backdrop-blur-sm group">
                <LaptopIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span>Windows</span>
              </a>
              <a href={downloadLinks.macos}
                 className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors bg-slate-800/50 px-3 py-2 rounded-lg hover:bg-slate-700/50 backdrop-blur-sm group">
                <MonitorIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span>macOS</span>
              </a>
              <a href={downloadLinks.android}
                 className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors bg-slate-800/50 px-3 py-2 rounded-lg hover:bg-slate-700/50 backdrop-blur-sm group">
                <PhoneIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span>Android</span>
              </a>
              <a href={downloadLinks.vps}
                 className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors bg-slate-800/50 px-3 py-2 rounded-lg hover:bg-slate-700/50 backdrop-blur-sm group">
                <MonitorIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span>VPS</span>
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-slate-300 hover:text-cyan-400 transition-colors bg-slate-800/50 p-2 rounded-lg backdrop-blur-sm"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 py-4 border-t border-slate-800/50 bg-slate-900/30 rounded-xl backdrop-blur-xl">
              <div className="space-y-4">
                <a href="https://geomyip.com" 
                   target="_blank"
                   rel="noopener noreferrer"
                   className="flex items-center space-x-2 text-green-400 hover:text-green-300 transition-colors text-sm bg-slate-800/60 px-4 py-3 rounded-lg font-semibold">
                  <GlobeIcon className="h-5 w-5" />
                  <span>Мой IP</span>
                </a>
                <p className="text-slate-300 font-medium text-sm">Скачать VPN клиент:</p>
                <div className="grid grid-cols-2 gap-3">
                  <a href={downloadLinks.windows} 
                     className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm bg-slate-800/60 px-3 py-2 rounded-lg">
                    <LaptopIcon className="h-4 w-4" />
                    <span>Windows</span>
                  </a>
                  <a href={downloadLinks.macos}
                     className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm bg-slate-800/60 px-3 py-2 rounded-lg">
                    <MonitorIcon className="h-4 w-4" />
                    <span>macOS</span>
                  </a>
                  <a href={downloadLinks.android}
                     className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm bg-slate-800/60 px-3 py-2 rounded-lg">
                    <PhoneIcon className="h-4 w-4" />
                    <span>Android</span>
                  </a>
                  <a href={downloadLinks.vps}
                     className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm bg-slate-800/60 px-3 py-2 rounded-lg">
                    <MonitorIcon className="h-4 w-4" />
                    <span>VPS Хостинг</span>
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 container mx-auto py-12 md:py-20 px-4">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Бесплатный VPN
            </span>
            <br />
            <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              для всех устройств
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-8 leading-relaxed">
            Получите мгновенный доступ к защищенному интернету. Бесплатный VPN для Windows, Android и iPhone.{' '}
            Без регистрации, без ограничений - просто скачайте и пользуйтесь!
          </p>
          
          {/* Stats */}
          <div className="flex justify-center items-center space-x-8 text-sm text-slate-400 mb-12">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4 text-green-400" />
              <span>99.9% Uptime</span>
            </div>
            <div className="flex items-center space-x-2">
              <Wifi className="h-4 w-4 text-blue-400" />
              <span>50+ Серверов</span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-purple-400" />
              <span>1M+ Пользователей</span>
            </div>
          </div>

        </div>

        {/* Main Generator Card */}
        <div className="max-w-4xl mx-auto mb-20">
          <Card className="bg-slate-800/40 border-slate-700/50 backdrop-blur-xl shadow-2xl shadow-purple-500/10 overflow-hidden">
            <CardContent className="p-8 md:p-12">
              {!generatedKey && !isGenerating && (
                <div className="text-center">
                  <Button 
                    onClick={generateVpnKey}
                    size="lg"
                    className="relative w-full max-w-md mx-auto bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 hover:from-cyan-400 hover:via-purple-400 hover:to-pink-400 text-white px-8 md:px-16 py-6 md:py-8 text-xl md:text-2xl font-bold shadow-2xl shadow-purple-500/30 hover:shadow-cyan-500/40 transition-all duration-500 transform hover:scale-105 rounded-2xl border-0 group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/30 to-pink-400/30 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                    <Shield className="mr-3 md:mr-4 h-6 md:h-8 w-6 md:w-8 relative z-10 group-hover:rotate-12 transition-transform duration-300" />
                    <span className="relative z-10">Сгенерировать VPN ключ</span>
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/20 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </Button>
                  
                  <p className="text-slate-400 text-sm md:text-base mt-6 px-4">
                    <RocketIcon className="inline w-5 h-5 mr-2" />
                    Получите бесплатный VPN ключ на 24 часа за несколько секунд
                  </p>
                </div>
              )}

              {isGenerating && (
                <div className="space-y-10">
                  <div className="text-center">
                    <div className="relative inline-block mb-6">
                      <div className="absolute inset-0 bg-cyan-400/30 rounded-full blur-2xl animate-pulse"></div>
                      <Loader2 className="h-16 w-16 animate-spin text-cyan-400 mx-auto relative z-10" />
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-4">Генерируем ваш VPN ключ...</h3>
                    <p className="text-slate-300 text-xl">
                      Магия происходит прямо сейчас <SparkleIcon className="inline w-6 h-6 ml-2" />
                    </p>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="relative">
                      <Progress value={progress} className="h-6 rounded-full bg-slate-700/50" />
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-full blur-sm"></div>
                    </div>
                    <p className="text-center text-slate-200 font-semibold text-xl">
                      {Math.round(progress)}% завершено
                    </p>
                    <p className="text-center text-slate-400 text-lg mt-2">
                      ⏱️ Примерное время: {generationTime} {generationTime === 1 ? 'секунда' : generationTime < 5 ? 'секунды' : 'секунд'}
                    </p>
                  </div>
                </div>
              )}

              {/* Maintenance Message Display */}
              {generatedKey === 'MAINTENANCE_MESSAGE' && (
                <Card className="w-full max-w-4xl bg-slate-900/60 border-slate-700/50 backdrop-blur-xl shadow-2xl">
                  <CardContent className="p-8">
                    <div className="text-center space-y-6">
                      <div className="mb-6">
                        <Loader2 className="h-16 w-16 text-cyan-400 mx-auto mb-4" />
                        <h3 className="text-2xl md:text-3xl font-bold text-white">
                          Техническое обслуживание
                        </h3>
                      </div>
                      
                      <div className="bg-slate-800/80 rounded-2xl p-6 border border-slate-600/50">
                        <p className="text-slate-200 text-lg md:text-xl leading-relaxed mb-6">
                          VPN Monster временно на техническом обслуживании.
                        </p>
                        <p className="text-slate-300 text-base md:text-lg mb-6">
                          Пока мы это делаем, вы можете получить бесплатный<br />
                          VPN от Hidezen VPN на сайте.
                        </p>
                        <a 
                          href="https://hidezen.com" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white px-8 py-4 text-xl font-bold rounded-xl shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 transform hover:scale-105"
                        >
                          Перейти на Hidezen.com
                          <ExternalLink className="ml-2 h-6 w-6" />
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Generated Key Display */}
              {generatedKey && generatedKey !== 'HIDEZEN_PROMO' && generatedKey !== 'MAINTENANCE_MESSAGE' && (
                <Card className="w-full max-w-4xl bg-slate-900/60 border-slate-700/50 backdrop-blur-xl shadow-2xl">
                  <CardContent className="p-8">
                    <div className="text-center space-y-6">
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center justify-center">
                        <TargetIcon className="w-8 h-8 mr-3" />
                        Ваш VPN ключ готов!
                      </h3>
                      
                      <div className="bg-slate-800/80 rounded-2xl p-6 border border-slate-600/50 hover:border-cyan-400/50 transition-all duration-300 shadow-inner">
                        <div className="text-center">
                          <code className="text-cyan-400 font-mono text-2xl md:text-3xl lg:text-4xl font-bold block mb-6 break-all">
                            {generatedKey}
                          </code>
                          <Button
                            onClick={() => copyToClipboard(generatedKey)}
                            variant="outline"
                            className="border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10 hover:border-cyan-400 transition-all duration-300 px-8 py-3 text-lg font-semibold rounded-lg backdrop-blur-sm"
                          >
                            {copied ? (
                              <>
                                <Check className="h-5 w-5 mr-2" />
                                Скопировано
                              </>
                            ) : (
                              <>
                                <Copy className="h-5 w-5 mr-2" />
                                Копировать
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                      
                      <p className="text-slate-400 text-sm md:text-base mt-4 px-2 flex items-center justify-center">
                        <BulbIcon className="w-5 h-5 mr-2" />
                        Нажмите на кнопку для копирования ключа в буфер обмена
                      </p>

                      <div className="mt-8 pt-6 border-t border-slate-700/50">
                        <a 
                          href="https://vpn.monster/ref/540" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group inline-block w-full max-w-md mx-auto bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-2xl p-6 hover:from-green-600/30 hover:to-emerald-600/30 hover:border-green-400/50 transition-all duration-300 transform hover:scale-105"
                        >
                          <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent group-hover:from-green-300 group-hover:to-emerald-300 font-bold text-xl md:text-2xl flex items-center justify-center">
                            <PartyIcon className="w-6 h-6 mr-2 text-green-400" />
                            Хотите больше? Скидка 70%!
                          </span>
                          <p className="text-slate-300 text-sm mt-2 group-hover:text-slate-200 transition-colors">
                            Получить premium доступ
                          </p>
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Hidezen VPN Advertisement */}
              {generatedKey === 'HIDEZEN_PROMO' && hidezenConfig && (
                <Card className="w-full max-w-4xl bg-indigo-900/80 border-indigo-600/50 backdrop-blur-xl shadow-2xl">
                  <CardContent className="p-8">
                    <div className="text-center space-y-6">
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
                        Специальное предложение для вас!
                      </h3>
                      
                      <div className="rounded-2xl p-6">
                        <p className="text-indigo-100 text-lg md:text-xl font-medium mb-6">
                          {hidezenConfig.message}
                        </p>
                        
                        <div className="space-y-4">
                          <a 
                            href={hidezenConfig.url}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-block w-full max-w-md mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                          >
                            <span className="flex items-center justify-center">
                              {hidezenConfig.hidezen_button_text || hidezenConfig.buttonText || hidezenConfig.button_text || 'Получить Hidezen VPN (90 дней бесплатно)'}
                            </span>
                          </a>
                        </div>
                      </div>
                      
                      <div className="text-indigo-200/80 text-base space-y-1">
                        <p>* Предложение доступно для пользователей из вашего региона</p>
                        {userDevice && (
                          <p>* Оптимизировано для {userDevice.os} устройств</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </div>

        {/* IP Information Block */}
        <div className="max-w-4xl mx-auto mb-20">
          <Card className="bg-slate-800/40 border-slate-700/50 backdrop-blur-xl shadow-2xl shadow-green-500/10 overflow-hidden">
            <CardContent className="p-8 md:p-10">
              <div className="flex items-start md:items-center space-x-4 mb-6">
                <div className="relative flex-shrink-0">
                  <GlobeIcon className="h-10 w-10 md:h-12 md:w-12 text-green-400" />
                  <div className="absolute -inset-2 bg-green-400/20 rounded-full blur-md"></div>
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                    Узнайте информацию о вашем IP-адресе
                  </h3>
                  <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                    Проверьте ваш реальный IP-адрес, местоположение, провайдера и другую полезную информацию о вашем интернет-подключении. 
                    Это важно для обеспечения вашей безопасности и конфиденциальности в сети.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-slate-700/50">
                <div className="flex items-center space-x-2 text-slate-400 text-sm">
                  <LockIcon className="w-4 h-4" />
                  <span>Бесплатная проверка IP и геолокации</span>
                </div>
                <a 
                  href="https://geomyip.com/ru/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-green-500/50 transition-all duration-300 transform hover:scale-105 group"
                >
                  <span>Проверить мой IP</span>
                  <ExternalLink className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section Divider */}
        <div className="max-w-6xl mx-auto my-32">
          <div className="relative flex items-center justify-center">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-cyan-400/50 via-purple-400/50 to-transparent"></div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="bg-gradient-to-r from-cyan-500 to-purple-500 w-16 h-1 rounded-full shadow-lg shadow-purple-500/50"></div>
            </div>
          </div>
        </div>

        {/* OS Support Section */}
        <div className="max-w-4xl mx-auto mb-40 mt-24">
          <h3 className="text-2xl md:text-3xl font-bold text-center text-white mb-20">
            Поддержка всех популярных операционных систем
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-slate-800/40 border border-slate-700/50 backdrop-blur-xl rounded-2xl p-6 text-center hover:bg-slate-700/40 transition-all duration-300 group shadow-xl hover:shadow-2xl hover:shadow-blue-500/20">
              <div className="relative inline-block mb-4">
                <div className="absolute inset-0 bg-blue-400/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <LaptopIcon className="h-12 w-12 mx-auto relative z-10 group-hover:scale-110 transition-transform" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">Бесплатный VPN для Windows</h4>
              <p className="text-slate-400 text-sm group-hover:text-slate-300 transition-colors">Полная поддержка всех версий Windows. Быстрая установка и настройка.</p>
            </div>
            
            <div className="bg-slate-800/40 border border-slate-700/50 backdrop-blur-xl rounded-2xl p-6 text-center hover:bg-slate-700/40 transition-all duration-300 group shadow-xl hover:shadow-2xl hover:shadow-green-500/20">
              <div className="relative inline-block mb-4">
                <div className="absolute inset-0 bg-green-400/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <PhoneIcon className="h-12 w-12 mx-auto relative z-10 group-hover:scale-110 transition-transform" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2 group-hover:text-green-400 transition-colors">Бесплатный VPN для Android</h4>
              <p className="text-slate-400 text-sm group-hover:text-slate-300 transition-colors">Защитите свой Android устройство. Простое подключение одним касанием.</p>
            </div>
            
            <div className="bg-slate-800/40 border border-slate-700/50 backdrop-blur-xl rounded-2xl p-6 text-center hover:bg-slate-700/40 transition-all duration-300 group shadow-xl hover:shadow-2xl hover:shadow-purple-500/20">
              <div className="relative inline-block mb-4">
                <div className="absolute inset-0 bg-purple-400/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <PhoneIcon className="h-12 w-12 mx-auto relative z-10 group-hover:scale-110 transition-transform" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">Бесплатный VPN для iPhone</h4>
              <p className="text-slate-400 text-sm group-hover:text-slate-300 transition-colors">Надежная защита для iOS устройств. Совместим со всеми версиями iPhone.</p>
            </div>
            
            <div className="bg-slate-800/40 border border-slate-700/50 backdrop-blur-xl rounded-2xl p-6 text-center hover:bg-slate-700/40 transition-all duration-300 group shadow-xl hover:shadow-2xl hover:shadow-pink-500/20">
              <div className="relative inline-block mb-4">
                <div className="absolute inset-0 bg-pink-400/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <MonitorIcon className="h-12 w-12 mx-auto relative z-10 group-hover:scale-110 transition-transform" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2 group-hover:text-pink-400 transition-colors">Бесплатный VPN для macOS</h4>
              <p className="text-slate-400 text-sm group-hover:text-slate-300 transition-colors">Полная интеграция с macOS. Высокая скорость и стабильность соединения.</p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          <Card className="bg-slate-800/30 border-slate-700/40 backdrop-blur-xl hover:bg-slate-700/40 transition-all duration-500 group shadow-xl hover:shadow-2xl hover:shadow-cyan-500/20">
            <CardContent className="p-8 text-center">
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <LightningIcon className="h-12 w-12 text-cyan-400 relative z-10 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500" />
              </div>
              <h3 className="text-white font-bold mb-4 text-xl group-hover:text-cyan-400 transition-colors">Молниеносно</h3>
              <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">Генерация ключа за секунды, без очередей и ожидания</p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/30 border-slate-700/40 backdrop-blur-xl hover:bg-slate-700/40 transition-all duration-500 group shadow-xl hover:shadow-2xl hover:shadow-purple-500/20">
            <CardContent className="p-8 text-center">
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-purple-400/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <GlobeIcon className="h-12 w-12 text-purple-400 relative z-10 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500" />
              </div>
              <h3 className="text-white font-bold mb-4 text-xl group-hover:text-purple-400 transition-colors">Глобально</h3>
              <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">Доступ к серверам по всему миру для лучшей скорости</p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/30 border-slate-700/40 backdrop-blur-xl hover:bg-slate-700/40 transition-all duration-500 group shadow-xl hover:shadow-2xl hover:shadow-pink-500/20">
            <CardContent className="p-8 text-center">
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-pink-400/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <LockIcon className="h-12 w-12 text-pink-400 relative z-10 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500" />
              </div>
              <h3 className="text-white font-bold mb-4 text-xl group-hover:text-pink-400 transition-colors">Безопасно</h3>
              <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">Военное шифрование для защиты ваших данных</p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/30 border-slate-700/40 backdrop-blur-xl hover:bg-slate-700/40 transition-all duration-500 group shadow-xl hover:shadow-2xl hover:shadow-green-500/20">
            <CardContent className="p-8 text-center">
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-green-400/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <FreeIcon className="h-12 w-12 text-green-400 relative z-10 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500" />
              </div>
              <h3 className="text-white font-bold mb-4 text-xl group-hover:text-green-400 transition-colors">Бесплатно</h3>
              <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">Полностью бесплатное использование, без скрытых платежей</p>
            </CardContent>
          </Card>
        </div>

        {/* Info Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Card className="bg-slate-800/30 border-slate-700/40 backdrop-blur-xl shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="relative">
                  <UsersIcon className="h-8 w-8 text-cyan-400" />
                  <div className="absolute -inset-1 bg-cyan-400/20 rounded-full blur-sm"></div>
                </div>
                <h3 className="text-2xl font-bold text-white">Кому подходит</h3>
              </div>
              <ul className="space-y-4 text-slate-300">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="leading-relaxed">Пользователям, которым нужен быстрый доступ к VPN без регистрации</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="leading-relaxed">Тестирование VPN сервиса перед покупкой премиум подписки</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-pink-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="leading-relaxed">Временная защита соединения в общественных Wi-Fi сетях</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="leading-relaxed">Обход географических ограничений для контента</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/30 border-slate-700/40 backdrop-blur-xl shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="relative">
                  <DownloadIcon className="h-8 w-8 text-green-400" />
                  <div className="absolute -inset-1 bg-green-400/20 rounded-full blur-sm"></div>
                </div>
                <h3 className="text-2xl font-bold text-white">Как использовать</h3>
              </div>
              <ol className="space-y-4 text-slate-300">
                <li className="flex items-start space-x-4">
                  <span className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">1</span>
                  <span className="leading-relaxed">Нажмите кнопку "Сгенерировать VPN ключ" и дождитесь завершения</span>
                </li>
                <li className="flex items-start space-x-4">
                  <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">2</span>
                  <span className="leading-relaxed">Скачайте VPN клиент для вашей операционной системы</span>
                </li>
                <li className="flex items-start space-x-4">
                  <span className="bg-gradient-to-r from-pink-500 to-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">3</span>
                  <span className="leading-relaxed">Скопируйте ключ, вставьте в приложение и наслаждайтесь защищенным интернетом</span>
                </li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default VpnGenerator;